<template>
    <Dropdown expanded paddingless>
        <template #trigger="{ open }">
            <slot name="trigger" :open="open" :selected-users="selectedUsers">
                <Button :active="open" :glass="props.glass" :loading="isLoading" class="tw-flex tw-gap-1">
                    <i class="mdi mdi-account-multiple-outline"></i>
                    Team members
                    <span
                        v-if="selectedMemberIds.length"
                        class="tw-border tw-border-gray-400 tw-ml-1 tw-w-5 tw-h-5 tw-flex tw-justify-center tw-items-center tw-rounded-full tw-text-xs tw-font-normal"
                    >
                        {{ selectedMemberIds.length }}
                    </span>
                </Button>
            </slot>
        </template>
        <div class="tw-w-[min(90vw,32rem)] tw-overflow-hidden tw-rounded-lg tw-bg-white">
            <div class="tw-border-b tw-p-3">
                <Textbox v-model="searchQuery" placeholder="Search team members..." />
            </div>
            <div v-if="isLoading" class="tw-px-4 tw-py-6 tw-text-center tw-text-xs tw-text-gray-500">
                Loading team members...
            </div>
            <div v-else-if="filteredTeamMembers.length" class="tw-max-h-72 tw-overflow-y-auto tw-py-2">
                <Checkbox
                    v-for="member in filteredTeamMembers"
                    :key="member.id"
                    v-model="selectedMemberIds"
                    :value="member.id"
                    class="tw-mx-3 tw-my-1.5 tw-w-[calc(100%-1.5rem)] tw-items-center"
                >
                    <UserAvatar :user="member" hide-name size="small" class="tw-ml-2" />
                    <div class="tw-ml-2 tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                        <span class="tw-truncate tw-text-xs tw-font-medium">{{ member.name || member.email }}</span>
                        <span v-if="member.email && member.name" class="tw-truncate tw-text-xs tw-text-gray-400">
                            {{ member.email }}
                        </span>
                    </div>
                    <span v-if="member.invite_pending" class="tw-ml-auto tw-text-xs tw-text-gray-400">
                        Invite pending
                    </span>
                </Checkbox>
            </div>
            <div v-else class="tw-px-4 tw-py-6 tw-text-center tw-text-xs tw-text-gray-500">
                {{ searchQuery.trim() ? 'No team members match your search.' : 'There are no team members available.' }}
            </div>
            <div class="tw-flex tw-items-center tw-justify-between tw-border-t tw-bg-gray-50 tw-px-3 tw-py-2">
                <span class="tw-text-xs tw-text-gray-500">
                    {{ selectedMemberIds.length }} team member{{ selectedMemberIds.length === 1 ? '' : 's' }} selected
                </span>
                <Button compact :disabled="selectedMemberIds.length === 0" @click="selectedMemberIds = []">
                    Clear selection
                </Button>
            </div>
        </div>
    </Dropdown>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import axios, { type AxiosError } from 'axios';
import { route } from 'ziggy-js';
import type { User } from '@/js/types/user.ts';
import { toast } from '@/js/utils.js';
import Button from '@/js/components/controls/Button.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import Dropdown from '@/js/components/controls/Dropdown.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import UserAvatar from '@/js/components/widgets/User.vue';
import { useDataStore } from '@/js/stores/DataStore.js';

type TeamMember = User & {
    id: number;
    invite_pending?: boolean;
};

const selectedMemberIds = defineModel<number[]>({
    required: true,
    default: () => [],
});

const props = withDefaults(
    defineProps<{
        glass?: boolean;
    }>(),
    {
        glass: false,
    }
);

defineSlots<{
    trigger?: (props: { open: boolean; selectedUsers: TeamMember[] }) => unknown;
}>();

const teamMembers = ref<TeamMember[]>([]);
const isLoading = ref(false);
const searchQuery = ref('');

const selectedUsers = computed<TeamMember[]>(() =>
    selectedMemberIds.value.flatMap((memberId) => {
        const member = teamMembers.value.find((availableMember) => availableMember.id === memberId);

        return member ? [member] : [];
    })
);

const filteredTeamMembers = computed<TeamMember[]>(() => {
    const normalizedQuery = searchQuery.value.trim().toLocaleLowerCase();

    return teamMembers.value
        .filter((member) => {
            const searchableText = `${member.name ?? ''} ${member.email ?? ''}`.toLocaleLowerCase();

            return searchableText.includes(normalizedQuery);
        })
        .sort((first, second) => (first.name || first.email || '').localeCompare(second.name || second.email || ''));
});

onMounted(() => {
    refreshTeamMembers();
});

const refreshTeamMembers = async (): Promise<void> => {
    isLoading.value = true;

    try {
        const { data } = await axios.get<TeamMember[]>(
            route('api.account.team', {
                account: useDataStore().get('account.id'),
            })
        );
        teamMembers.value = data;
    } catch (error) {
        const message =
            (error as AxiosError<{ message?: string }>).response?.data?.message ||
            'An error occurred while fetching team members.';
        toast('Error', message, 'error');
    } finally {
        isLoading.value = false;
    }
};
</script>
